"use client";

import { useState, useEffect } from 'react';
// Using native button element since the Button component is designed for links

interface PasswordProtectionProps {
  pageId: number;
  onPasswordSuccess: () => void;
  className?: string;
}

export default function PasswordProtection({ 
  pageId, 
  onPasswordSuccess, 
  className = "" 
}: PasswordProtectionProps) {
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 3;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!password.trim()) {
      setError("Please enter a password");
      return;
    }

    if (attempts >= maxAttempts) {
      setError("Too many failed attempts. Please try again later.");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const response = await fetch('/api/password-verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          pageId: pageId,
          password: password
        })
      });

      const data = await response.json();

      if (data.success) {
        // Store password success in sessionStorage for this page
        sessionStorage.setItem(`wp_page_${pageId}_auth`, 'true');
        onPasswordSuccess();
      } else {
        setAttempts(prev => prev + 1);
        setError(data.message || "Invalid password. Please try again.");
        setPassword("");
      }
    } catch (error) {
      console.error('Password verification error:', error);
      setError("An error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit(e as any);
    }
  };

  return (
    <div className={`max-w-md mx-auto p-6 bg-white rounded-lg shadow-md ${className}`}>
      <div className="text-center mb-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-2">
          Protected Content
        </h2>
        <p className="text-gray-600">
          This content is password protected. Please enter the password to view.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
            Password
          </label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={handleKeyPress}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter password"
            disabled={isLoading || attempts >= maxAttempts}
          />
        </div>

        {error && (
          <div className="text-red-600 text-sm bg-red-50 p-2 rounded">
            {error}
          </div>
        )}

        {attempts >= maxAttempts && (
          <div className="text-orange-600 text-sm bg-orange-50 p-2 rounded">
            Maximum attempts reached. Please contact the administrator if you need access.
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading || attempts >= maxAttempts}
          className="w-full bg-[#1757A3] text-white font-semibold py-3 px-6 rounded-md hover:bg-[#1757A3]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isLoading ? 'Verifying...' : 'Submit'}
        </button>
      </form>

      {attempts > 0 && attempts < maxAttempts && (
        <div className="mt-4 text-sm text-gray-500 text-center">
          {maxAttempts - attempts} attempts remaining
        </div>
      )}
    </div>
  );
} 